/**
 * ParcoursLivePreview
 * ─────────────────────────────────────────────────────────────────
 * Side card "data-dense" pour les pages parcours rénovation. Chaque
 * slug a une variante visuelle dédiée (mini-DPE, scénarios chiffrés,
 * pipeline travaux, tableau de bord MAR) — pattern inspiré de
 * AgriculteurPreview / ArtisanDashboardPreview.
 *
 * Tous les chiffres affichés sont :
 *  - explicitement indicatifs ("estimation", "scénario type")
 *  - cohérents avec la config parcours-particuliers.ts
 *  - prudents (formulations "selon barème", "jusqu'à") cf. AGENTS §8
 */

import { motion } from 'framer-motion';
import { CountUp } from '../motion/CountUp';
import { easeStripe } from '../../lib/motion';
import { cn } from '../../lib/cn';

interface Props {
  slug: string;
}

export function ParcoursLivePreview({ slug }: Props) {
  switch (slug) {
    case 'bilan-energetique':
      return <BilanPreview />;
    case 'audit-energetique':
      return <AuditPreview />;
    case 'renovation-globale':
      return <RenovationGlobalePreview />;
    case 'mon-accompagnateur-renov':
      return <MARPreview />;
    default:
      return null;
  }
}

// ════════════════════════════════════════════════════════════════
// Wrappers partagés
// ════════════════════════════════════════════════════════════════

function PreviewShell({
  headerLabel,
  headerSub,
  badge,
  badgeTone = 'brand',
  children,
}: {
  headerLabel: string;
  headerSub: string;
  badge: string;
  badgeTone?: 'brand' | 'accent' | 'lavender';
  children: React.ReactNode;
}) {
  const badgeClass =
    badgeTone === 'accent'
      ? 'bg-accent-50 text-accent-700 ring-accent-200/50 [&_.dot]:bg-accent-500'
      : badgeTone === 'lavender'
        ? 'bg-lavender-50 text-lavender-700 ring-lavender-200/50 [&_.dot]:bg-lavender-500'
        : 'bg-brand-50 text-brand-700 ring-brand-200/50 [&_.dot]:bg-brand-500';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-brand-200/60 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            {headerLabel}
          </p>
          <p className="mt-0.5 truncate text-xs text-stone-500">{headerSub}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset',
            badgeClass,
          )}
        >
          <span className="dot h-1 w-1 rounded-full animate-glow-pulse" />
          {badge}
        </span>
      </div>
      {children}
    </motion.div>
  );
}

function DataRow({
  label,
  value,
  status,
  tone = 'brand',
}: {
  label: string;
  value: string;
  status?: string;
  tone?: 'brand' | 'accent' | 'lavender' | 'stone';
}) {
  const toneText =
    tone === 'brand'
      ? 'text-brand-700'
      : tone === 'lavender'
        ? 'text-lavender-700'
        : tone === 'accent'
          ? 'text-accent-700'
          : 'text-stone-600';

  return (
    <div className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs text-stone-700">{label}</p>
        {status && (
          <p
            className={cn(
              'mt-0.5 text-[10px] font-semibold uppercase tracking-wider',
              toneText,
            )}
          >
            {status}
          </p>
        )}
      </div>
      <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
        {value}
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. Bilan énergétique — mini DPE actuel → projeté + leviers
// ════════════════════════════════════════════════════════════════

const DPE_COLORS: Record<string, string> = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-lime-400 text-ink',
  C: 'bg-yellow-300 text-ink',
  D: 'bg-amber-400 text-ink',
  E: 'bg-orange-500 text-white',
  F: 'bg-red-500 text-white',
  G: 'bg-red-700 text-white',
};

function DpeBadge({ letter, dim = false }: { letter: string; dim?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-md font-display text-base font-black tracking-tight ring-1 ring-inset ring-stone-200/50',
        DPE_COLORS[letter] ?? 'bg-stone-200 text-stone-600',
        dim && 'opacity-40 saturate-50',
      )}
    >
      {letter}
    </span>
  );
}

function BilanPreview() {
  return (
    <PreviewShell
      headerLabel="Aperçu bilan énergétique"
      headerSub="Maison · 1985 · 120 m² · chauffage gaz"
      badge="Scénario type"
    >
      <div className="mt-5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
          Performance estimée
        </p>
        <div className="mt-2 flex items-center gap-3">
          <DpeBadge letter="E" />
          <svg
            className="h-3 w-6 shrink-0 text-stone-300"
            viewBox="0 0 24 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden
          >
            <path d="M2 6h18m-4-4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <DpeBadge letter="C" />
          <span className="ml-1 font-mono text-[11px] text-stone-500">après travaux</span>
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
          Leviers identifiés
        </p>
        <div className="space-y-1.5">
          <DataRow label="Isolation combles perdus" value="−18 %" status="Priorité 1" tone="brand" />
          <DataRow label="Remplacement PAC air/eau" value="−27 %" status="Priorité 2" tone="accent" />
          <DataRow label="Menuiseries double vitrage" value="−8 %" status="Priorité 3" tone="lavender" />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Aides estimées</p>
        <p className="font-display text-base font-bold text-ink tabular-nums">
          <CountUp end={11400} suffix=" €" />
        </p>
      </div>
    </PreviewShell>
  );
}

// ════════════════════════════════════════════════════════════════
// 2. Audit énergétique — 2 scénarios chiffrés + DPE projeté
// ════════════════════════════════════════════════════════════════

function AuditPreview() {
  return (
    <PreviewShell
      headerLabel="Rapport d'audit · Extrait"
      headerSub="Maison F · 110 m² · Drôme"
      badge="Audit type"
      badgeTone="accent"
    >
      <div className="mt-5 grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-stone-200/70 bg-stone-50/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500">
            Scénario A · étapes
          </p>
          <p className="mt-2 font-display text-lg font-bold text-ink tabular-nums">
            <CountUp end={28000} suffix=" €" />
          </p>
          <div className="mt-2 flex items-center gap-1.5">
            <DpeBadge letter="F" dim />
            <span className="text-[10px] text-stone-400">→</span>
            <DpeBadge letter="D" />
          </div>
          <p className="mt-2 text-[10px] text-stone-500">2 sauts · gestes séparés</p>
        </div>

        <div className="rounded-lg border border-brand-200/60 bg-brand-50/40 p-3 ring-1 ring-inset ring-brand-200/40">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-700">
            Scénario B · global
          </p>
          <p className="mt-2 font-display text-lg font-bold text-ink tabular-nums">
            <CountUp end={52000} suffix=" €" />
          </p>
          <div className="mt-2 flex items-center gap-1.5">
            <DpeBadge letter="F" dim />
            <span className="text-[10px] text-stone-400">→</span>
            <DpeBadge letter="B" />
          </div>
          <p className="mt-2 text-[10px] text-brand-700">4 sauts · bouquet</p>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <DataRow label="Audit énergétique réglementaire" value="850 €" status="Forfait moyen" tone="stone" />
        <DataRow label="MaPrimeRénov’ audit (forfait Anah)" value="−500 €" status="Selon revenus" tone="brand" />
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Reste à charge audit</p>
        <p className="font-mono text-[12px] font-semibold text-accent-700 tabular-nums">
          <CountUp end={350} suffix=" €" />
        </p>
      </div>
    </PreviewShell>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. Rénovation globale — pipeline travaux + cumul aides
// ════════════════════════════════════════════════════════════════

function RenovationGlobalePreview() {
  return (
    <PreviewShell
      headerLabel="Plan rénovation d'ampleur"
      headerSub="MPR Accompagné · 4 sauts DPE"
      badge="En cours"
      badgeTone="accent"
    >
      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={38400} suffix=" €" />
        </span>
        <span className="text-xs text-stone-500">aides cumulées estimées</span>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
          Cumul détaillé
        </p>
        <div className="space-y-1.5">
          <DataRow label="MaPrimeRénov’ Accompagné" value="22 000 €" status="Anah" tone="brand" />
          <DataRow label="Coup de pouce CEE rénovation" value="9 800 €" status="Obligé" tone="accent" />
          <DataRow label="Éco-PTZ complémentaire" value="50 000 €" status="Sur 20 ans" tone="lavender" />
          <DataRow label="TVA 5,5 % vs 20 %" value="6 600 €" status="Auto" tone="stone" />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Reste à charge sur 60 k€ travaux</p>
        <p className="font-mono text-[12px] font-semibold text-brand-700 tabular-nums">
          ≈ 21 600 €
        </p>
      </div>
    </PreviewShell>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. Mon Accompagnateur Rénov' — phases + interlocuteurs
// ════════════════════════════════════════════════════════════════

function MARPreview() {
  return (
    <PreviewShell
      headerLabel="Mission Accompagnateur Rénov'"
      headerSub="Pavillon · Eure-et-Loir · MPR Accompagné"
      badge="Agréé Anah"
      badgeTone="lavender"
    >
      <div className="mt-5">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
          Phases de la mission
        </p>
        <ol className="space-y-2">
          {[
            { step: '01', title: 'Audit énergétique initial', state: 'Terminé', tone: 'brand' as const },
            { step: '02', title: 'Plan de financement', state: 'Terminé', tone: 'brand' as const },
            { step: '03', title: 'Dépôt MPR Accompagné', state: 'En cours', tone: 'accent' as const },
            { step: '04', title: 'Suivi chantier RGE', state: 'À venir', tone: 'stone' as const },
            { step: '05', title: 'Audit final & versement', state: 'À venir', tone: 'stone' as const },
          ].map((p) => (
            <li
              key={p.step}
              className="flex items-center gap-3 rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
            >
              <span className="font-mono text-[10px] font-semibold text-stone-400 tabular-nums">
                {p.step}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs text-stone-700">{p.title}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                  p.tone === 'brand' && 'bg-brand-50 text-brand-700 ring-brand-200/50',
                  p.tone === 'accent' && 'bg-accent-50 text-accent-700 ring-accent-200/50',
                  p.tone === 'stone' && 'bg-stone-100 text-stone-500 ring-stone-200/60',
                )}
              >
                {p.state}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Forfait MAR · reste à charge</p>
        <p className="font-mono text-[12px] font-semibold text-lavender-700 tabular-nums">
          ≈ 480 € après MPR
        </p>
      </div>
    </PreviewShell>
  );
}
